import { useState } from 'react';
import { webhooks as webhooksApi } from '../api/resources.js';
import type { WebhookDelivery } from '../api/resources.js';
import {
  Banner,
  Card,
  CopyButton,
  Empty,
  ErrorState,
  Loading,
  StatusPill,
} from '../components/ui.js';
import { useToast } from '../components/toast.js';
import { formatDateTime, formatRelative } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

const EVENT_OPTIONS = [
  'message.received',
  'message.sent',
  'message.updated',
  'instance.connected',
  'instance.logged_out',
  'message.*',
  'instance.*',
  '*',
];

/** Webhook platform: endpoints, debugger, DLQ and replay (PRD §42, §45, §46, §47). */
export function WebhooksPage() {
  const toast = useToast();
  const [tab, setTab] = useState<'endpoints' | 'deliveries' | 'dlq'>('endpoints');
  const [form, setForm] = useState({ url: '', events: ['message.received'] });
  const [revealed, setRevealed] = useState<{ url: string; secret: string } | null>(null);

  const endpoints = useAsync(() => webhooksApi.list(), []);
  const deliveries = useAsync(
    () => (tab === 'deliveries' ? webhooksApi.deliveries({ limit: '100' }) : Promise.resolve(null)),
    [tab],
  );
  const dlq = useAsync(() => (tab === 'dlq' ? webhooksApi.dlq() : Promise.resolve(null)), [tab]);

  const create = useAction(async () => {
    const created = await webhooksApi.create(form.url, form.events);
    // Shown once, in a panel the customer must actively dismiss (PRD §43).
    setRevealed({ url: created.url, secret: created.secret });
    setForm({ url: '', events: ['message.received'] });
    endpoints.reload();
  });

  const sendTest = useAction(async (endpointId: string, type: string) => {
    await webhooksApi.sendTest(endpointId, type);
    toast.success('Evento de teste enviado', 'Acompanhe em Entregas.');
  });

  const replay = useAction(async (deliveryId: string) => {
    await webhooksApi.replay(deliveryId);
    toast.success('Reenvio agendado', 'O evento original será entregue novamente.');
    dlq.reload();
  });

  const toggle = useAction(async (id: string, status: 'ACTIVE' | 'PAUSED') => {
    await webhooksApi.update(id, { status });
    endpoints.reload();
  });

  const remove = useAction(async (id: string) => {
    await webhooksApi.remove(id);
    toast.success('Endpoint removido');
    endpoints.reload();
  });

  return (
    <>
      {revealed && (
        <Card
          title="Segredo de assinatura"
          description="Exibido apenas agora. A Talkium não consegue mostrá-lo novamente."
          actions={
            <button type="button" className="btn btn-sm" onClick={() => setRevealed(null)}>
              Já guardei
            </button>
          }
        >
          <Banner tone="warn">
            Use este segredo para validar a assinatura <code>X-Talkium-Signature</code> de cada
            entrega.
          </Banner>
          <div className="row" style={{ marginTop: 12 }}>
            <code className="mono">{revealed.secret}</code>
            <CopyButton value={revealed.secret} />
          </div>
        </Card>
      )}

      <Card title="Novo endpoint" description="Somente HTTPS. Redes internas são recusadas.">
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void create.run();
          }}
        >
          <div style={{ flex: '2 1 260px' }}>
            <label htmlFor="webhook-url">URL</label>
            <input
              id="webhook-url"
              type="url"
              value={form.url}
              onChange={(event) => setForm((c) => ({ ...c, url: event.target.value }))}
              placeholder="https://seu-sistema.com/webhooks/talkium"
              required
            />
          </div>
          <div>
            <label htmlFor="webhook-events">Eventos</label>
            <select
              id="webhook-events"
              multiple
              value={form.events}
              onChange={(event) =>
                setForm((c) => ({
                  ...c,
                  events: [...event.target.selectedOptions].map((option) => option.value),
                }))
              }
              style={{ minHeight: 88 }}
            >
              {EVENT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>
          <button type="submit" className="btn btn-primary" disabled={create.pending}>
            {create.pending ? 'Criando…' : 'Criar endpoint'}
          </button>
        </form>
        {create.error != null && (
          <div style={{ marginTop: 12 }}>
            <ErrorState error={create.error} />
          </div>
        )}
      </Card>

      <Card
        bodyless
        title={
          <div className="tabs" style={{ border: 'none' }}>
            {(
              [
                ['endpoints', 'Endpoints'],
                ['deliveries', 'Entregas'],
                ['dlq', 'Dead letter queue'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={`tab ${tab === key ? 'active' : ''}`}
                onClick={() => setTab(key)}
              >
                {label}
              </button>
            ))}
          </div>
        }
      >
        {tab === 'endpoints' &&
          (endpoints.loading ? (
            <Loading />
          ) : endpoints.error ? (
            <ErrorState error={endpoints.error} onRetry={endpoints.reload} />
          ) : endpoints.data?.data.length === 0 ? (
            <Empty title="Nenhum endpoint configurado">
              <p className="muted">Crie um acima para começar a receber eventos.</p>
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>URL</th>
                    <th>Eventos</th>
                    <th>Status</th>
                    <th>Falhas seguidas</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(endpoints.data?.data ?? []).map((endpoint) => (
                    <tr key={endpoint.id}>
                      <td>
                        <div className="mono">{endpoint.url}</div>
                        <div className="subtle">
                          Último sucesso {formatRelative(endpoint.lastSuccessAt)}
                        </div>
                      </td>
                      <td>
                        {endpoint.events.map((event) => (
                          <span
                            key={event}
                            className="pill pill-neutral"
                            style={{ marginRight: 4 }}
                          >
                            {event}
                          </span>
                        ))}
                      </td>
                      <td>
                        <StatusPill status={endpoint.status} label={endpoint.status} />
                      </td>
                      <td className={endpoint.failureCount > 0 ? 'pill pill-warn' : 'subtle'}>
                        {endpoint.failureCount}
                      </td>
                      <td>
                        <div className="row">
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                              void sendTest.run(endpoint.id, endpoint.events[0] ?? '*')
                            }
                          >
                            Testar
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                              void toggle.run(
                                endpoint.id,
                                endpoint.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE',
                              )
                            }
                          >
                            {endpoint.status === 'ACTIVE' ? 'Pausar' : 'Ativar'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => {
                              if (confirm('Remover este endpoint?')) void remove.run(endpoint.id);
                            }}
                          >
                            Remover
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

        {tab === 'deliveries' && <DeliveryTable state={deliveries} />}

        {tab === 'dlq' &&
          (dlq.loading ? (
            <Loading />
          ) : dlq.data?.data.length === 0 ? (
            <Empty title="Dead letter queue vazia">
              <p className="muted">Nenhuma entrega esgotou as tentativas.</p>
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Criada</th>
                    <th>Tentativas</th>
                    <th>Último status</th>
                    <th>Erro</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(dlq.data?.data ?? []).map((delivery) => (
                    <tr key={delivery.id}>
                      <td className="subtle">{formatDateTime(delivery.createdAt)}</td>
                      <td>{delivery.attempt}</td>
                      <td>{delivery.statusCode ?? '—'}</td>
                      <td className="subtle">{delivery.errorMessage ?? '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => void replay.run(delivery.id)}
                          disabled={replay.pending}
                        >
                          Reenviar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
      </Card>
    </>
  );
}

function DeliveryTable({
  state,
}: {
  state: {
    loading: boolean;
    error: unknown;
    data: { data: WebhookDelivery[] } | null | undefined;
    reload: () => void;
  };
}) {
  if (state.loading) return <Loading />;
  if (state.error) return <ErrorState error={state.error} onRetry={state.reload} />;

  const rows = state.data?.data ?? [];
  if (rows.length === 0) return <Empty title="Nenhuma entrega registrada" />;

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Criada</th>
            <th>Status</th>
            <th>Tentativa</th>
            <th>HTTP</th>
            <th>Latência</th>
            <th>Próxima tentativa</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((delivery) => (
            <tr key={delivery.id}>
              <td className="subtle">{formatDateTime(delivery.createdAt)}</td>
              <td>
                <StatusPill status={delivery.status} label={delivery.status} />
              </td>
              <td>{delivery.attempt}</td>
              <td>{delivery.statusCode ?? '—'}</td>
              <td className="subtle">
                {delivery.latencyMs != null ? `${delivery.latencyMs} ms` : '—'}
              </td>
              <td className="subtle">
                {delivery.nextRetryAt ? formatRelative(delivery.nextRetryAt) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
